import { gradients, MOTION, RADIUS, SHADOW } from "@elyxeer/ui";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import Box from "@mui/material/Box";
import { useState } from "react";

/**
 * The welcome video, as a click-to-play facade over a YouTube embed.
 *
 * The facade is not decoration. The member app is a WebView bundle: dropping YouTube's
 * iframe into the first screen after sign-in pulls several hundred kilobytes of
 * third-party player onto a page that most members will scroll past, and it does it
 * before anything on the page is interactive. A poster image and a button cost one
 * request, and the player only arrives if somebody actually wants to watch.
 *
 * `youtube-nocookie.com` is the origin on purpose — nothing is set until playback
 * starts. `playsinline` matters for iOS, where the default is to take the video
 * fullscreen and hand the member a system player with no way back to the page.
 */
export function WelcomeVideo({
  videoId,
  title,
  playLabel,
  portrait = false,
}: {
  videoId: string;
  /** A Short is 9:16; a landscape box would pillarbox it down to a sliver. */
  portrait?: boolean;
  /** Accessible name of the player. Resolved copy — this component has no catalogue. */
  title: string;
  playLabel: string;
}) {
  const [playing, setPlaying] = useState(false);
  // maxres does not exist for every upload; hq always does.
  const [poster, setPoster] = useState(
    `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
  );

  return (
    <Box
      data-testid="welcome.video"
      sx={{
        position: "relative",
        width: "100%",
        // Capped so a portrait video does not run several screens tall on desktop.
        maxWidth: portrait ? 360 : undefined,
        mx: portrait ? "auto" : undefined,
        // A 16:9 box reserved up front, so the poster and the player that replaces it
        // occupy exactly the same space and nothing below them moves.
        aspectRatio: portrait ? "9 / 16" : "16 / 9",
        overflow: "hidden",
        borderRadius: `${RADIUS.lg}px`,
        // WebKit promotes the YouTube iframe to its own compositing layer, and a
        // composited child is clipped by the *layer*, not by the parent's border
        // radius — the corners leak square, which is at its most obvious against the
        // welcome screen's dark band. Giving this box a layer of its own puts the
        // rounding back on the same side of the compositor as the thing it clips.
        transform: "translateZ(0)",
        isolation: "isolate",
        backgroundColor: "surface.inset",
        boxShadow: SHADOW.card,
      }}
    >
      {playing ? (
        <Box
          component="iframe"
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0&modestbranding=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          sx={{ width: "100%", height: "100%", border: 0, display: "block" }}
        />
      ) : (
        <Box
          component="button"
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={playLabel}
          data-testid="welcome.video-play"
          sx={{
            position: "absolute",
            inset: 0,
            padding: 0,
            border: 0,
            cursor: "pointer",
            backgroundColor: "transparent",
            display: "grid",
            placeItems: "center",
            "&:hover .welcome-video-play": { transform: "scale(1.06)" },
          }}
        >
          <Box
            component="img"
            src={poster}
            alt=""
            onError={() =>
              setPoster(`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`)
            }
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
          {/* A scrim, so the play button keeps its contrast whatever frame YouTube
              chose for the thumbnail. */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              // The brand's own photo filter, so the thumbnail sits in the same
              // world as every other image in the app.
              backgroundImage: gradients.photoOverlay,
            }}
          />
          <Box
            className="welcome-video-play"
            sx={{
              position: "relative",
              display: "grid",
              placeItems: "center",
              width: { xs: 64, md: 80 },
              height: { xs: 64, md: 80 },
              borderRadius: `${RADIUS.full}px`,
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              boxShadow: SHADOW.raised,
              transition: `transform ${MOTION.transition}`,
            }}
          >
            <PlayArrowRoundedIcon sx={{ fontSize: { xs: 36, md: 44 } }} />
          </Box>
        </Box>
      )}
    </Box>
  );
}
